import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    deleteV2AdminUsersByUserIdVoiceEnrollment: build.mutation<
      DeleteV2AdminUsersByUserIdVoiceEnrollmentApiResponse,
      DeleteV2AdminUsersByUserIdVoiceEnrollmentApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/admin/users/${queryArg.userId}/voice-enrollment`,
        method: "DELETE",
      }),
    }),
    getV2AdminUsersByUserIdVoiceEnrollment: build.query<
      GetV2AdminUsersByUserIdVoiceEnrollmentApiResponse,
      GetV2AdminUsersByUserIdVoiceEnrollmentApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/admin/users/${queryArg.userId}/voice-enrollment`,
      }),
    }),
    getV2ProviderBillingInfo: build.query<
      GetV2ProviderBillingInfoApiResponse,
      GetV2ProviderBillingInfoApiArg
    >({
      query: () => ({ url: `/v2/provider/billing-info` }),
    }),
    patchV2ProviderBillingInfo: build.mutation<
      PatchV2ProviderBillingInfoApiResponse,
      PatchV2ProviderBillingInfoApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/billing-info`,
        method: "PATCH",
        body: queryArg.updateProviderBillingInfoRequest,
      }),
    }),
    getV2ProviderMarketingCustomizations: build.query<
      GetV2ProviderMarketingCustomizationsApiResponse,
      GetV2ProviderMarketingCustomizationsApiArg
    >({
      query: () => ({ url: `/v2/provider/marketing-customizations` }),
    }),
    postV2ProviderPhoneVerificationSend: build.mutation<
      PostV2ProviderPhoneVerificationSendApiResponse,
      PostV2ProviderPhoneVerificationSendApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/phone-verification/send`,
        method: "POST",
        body: queryArg.sendVerificationCodeRequest,
      }),
    }),
    getV2ProviderSubscriptionDetails: build.query<
      GetV2ProviderSubscriptionDetailsApiResponse,
      GetV2ProviderSubscriptionDetailsApiArg
    >({
      query: () => ({ url: `/v2/provider/subscription-details` }),
    }),
    getV2ProviderUserInfo: build.query<
      GetV2ProviderUserInfoApiResponse,
      GetV2ProviderUserInfoApiArg
    >({
      query: () => ({ url: `/v2/provider/user-info` }),
    }),
    postV2ProviderUsers: build.mutation<
      PostV2ProviderUsersApiResponse,
      PostV2ProviderUsersApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/users`,
        method: "POST",
        body: queryArg.createUserRequest,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type DeleteV2AdminUsersByUserIdVoiceEnrollmentApiResponse =
  /** status 200 OK */ VoiceEnrollmentResetResponse;
export type DeleteV2AdminUsersByUserIdVoiceEnrollmentApiArg = {
  userId: string;
};
export type GetV2AdminUsersByUserIdVoiceEnrollmentApiResponse =
  /** status 200 OK */ VoiceEnrollmentStatusResponse;
export type GetV2AdminUsersByUserIdVoiceEnrollmentApiArg = {
  userId: string;
};
export type GetV2ProviderBillingInfoApiResponse =
  /** status 200 OK */ ProviderBillingInfoResponse;
export type GetV2ProviderBillingInfoApiArg = void;
export type PatchV2ProviderBillingInfoApiResponse =
  /** status 200 OK */ ProviderBillingInfoResponse;
export type PatchV2ProviderBillingInfoApiArg = {
  updateProviderBillingInfoRequest: UpdateProviderBillingInfoRequest;
};
export type GetV2ProviderMarketingCustomizationsApiResponse =
  /** status 200 OK */ MarketingCustomizationsResponse;
export type GetV2ProviderMarketingCustomizationsApiArg = void;
export type PostV2ProviderPhoneVerificationSendApiResponse =
  /** status 200 OK */ SendVerificationCodeResponse;
export type PostV2ProviderPhoneVerificationSendApiArg = {
  sendVerificationCodeRequest: SendVerificationCodeRequest;
};
export type GetV2ProviderSubscriptionDetailsApiResponse =
  /** status 200 OK */ SubscriptionDetailsResponse;
export type GetV2ProviderSubscriptionDetailsApiArg = void;
export type GetV2ProviderUserInfoApiResponse =
  /** status 200 OK */ UserInfoResponse;
export type GetV2ProviderUserInfoApiArg = void;
export type PostV2ProviderUsersApiResponse =
  /** status 201 Created */ CreateUserResponse;
export type PostV2ProviderUsersApiArg = {
  createUserRequest: CreateUserRequest;
};
export type VoiceEnrollmentResetResponse = {
  /** Number of enrolled embeddings deleted from Supabase */
  enrolled_deleted: number;
  /** Whether the MongoDB asr_enrollment_status field was successfully removed */
  mongo_updated: boolean;
  /** Number of session embeddings deleted from Supabase */
  session_deleted: number;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type ServerErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the server error */
  status?: number;
};
export type VoiceEnrollmentStatusResponse = {
  /** Number of enrollment session data points associated with the user */
  data_point_count: number;
  /** ISO format datetime when the user was enrolled, or None if not enrolled */
  enrolled_at?: string | null;
  /** Whether the user has been enrolled for voice recognition */
  is_enrolled: boolean;
};
export type ProviderLicenseType =
  | "MD"
  | "DO"
  | "PhD"
  | "PsyD"
  | "LCSW"
  | "LMSW"
  | "LISW"
  | "LICSW"
  | "LCSW-C"
  | "LMFT"
  | "LPC"
  | "LCPC"
  | "LPCC"
  | "LMHC"
  | "LCMHC"
  | "NP"
  | "PMHNP"
  | "APRN"
  | "CNS"
  | "PA"
  | "RN"
  | "LADC"
  | "LCADC"
  | "CADC"
  | "BCBA"
  | "PSYCH_ASSOC"
  | "PASTORAL"
  | "OTHER";
export type ProviderBillingInfoResponse = {
  accept_insurance_payment_as_full?: string | null;
  /** Override org default billing entity for claims */
  billing_entity_id?: string | null;
  billing_npi_number?: string | null;
  city?: string | null;
  communications_name?: string | null;
  facility_address?: string | null;
  facility_address2?: string | null;
  fax_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  license_number?: string | null;
  license_state?: string | null;
  /** Provider license type (MD, DO, LCSW, etc.) */
  license_type?: ProviderLicenseType | null;
  npi_number?: string | null;
  phone?: string | null;
  place_of_service?: string | null;
  /** Override org default service facility for claims */
  service_facility_id?: string | null;
  signing_name?: string | null;
  state?: string | null;
  tax_id?: string | null;
  tax_id_type?: string | null;
  /** Rendering provider taxonomy code for claims */
  taxonomy_code?: string | null;
  zip?: string | null;
};
export type ProviderLicenseType2 =
  | "MD"
  | "DO"
  | "PhD"
  | "PsyD"
  | "LCSW"
  | "LMSW"
  | "LISW"
  | "LICSW"
  | "LCSW-C"
  | "LMFT"
  | "LPC"
  | "LCPC"
  | "LPCC"
  | "LMHC"
  | "LCMHC"
  | "NP"
  | "PMHNP"
  | "APRN"
  | "CNS"
  | "PA"
  | "RN"
  | "LADC"
  | "LCADC"
  | "CADC"
  | "BCBA"
  | "PSYCH_ASSOC"
  | "PASTORAL"
  | "OTHER";
export type UpdateProviderBillingInfoRequest = {
  accept_insurance_payment_as_full?: string | null;
  /** Override org default billing entity for claims */
  billing_entity_id?: string | null;
  billing_npi_number?: string | null;
  city?: string | null;
  communications_name?: string | null;
  facility_address?: string | null;
  facility_address2?: string | null;
  fax_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  license_number?: string | null;
  license_state?: string | null;
  /** Provider license type (MD, DO, LCSW, etc.) */
  license_type?: ProviderLicenseType2 | null;
  npi_number?: string | null;
  phone?: string | null;
  place_of_service?: string | null;
  /** Override org default service facility for claims */
  service_facility_id?: string | null;
  signing_name?: string | null;
  state?: string | null;
  tax_id?: string | null;
  tax_id_type?: string | null;
  /** Rendering provider taxonomy code for claims */
  taxonomy_code?: string | null;
  zip?: string | null;
};
export type MarketingCustomizationsResponse = {
  /** The user's marketing customizations as a JSON object */
  customizations: {
    [key: string]: any;
  };
};
export type SendVerificationCodeResponse = {
  /** The 4-digit verification code */
  code: string;
  /** If true, frontend should auto-fill the code for demo/test mode */
  demo?: boolean | null;
  /** Error code when success is false (e.g., 'duplicate_phone_number') */
  error?: string | null;
  /** Status message */
  message: string;
  /** Whether the code was sent successfully */
  success: boolean;
};
export type SendVerificationCodeRequest = {
  /** User's email address. If @jotpsych.com, bypasses SMS for demo accounts */
  email?: string | null;
  /** Phone number in E.164 format (e.g., +15551234567) */
  phone_number: string;
};
export type SubscriptionDetailsResponse = {
  /** Whether subscription will cancel at period end */
  cancel_at_period_end?: boolean | null;
  /** Unix timestamp when subscription was canceled */
  canceled_at?: number | null;
  /** Unix timestamp when subscription was created */
  created?: number | null;
  /** Unix timestamp of current billing period end */
  current_period_end?: number | null;
  /** Unix timestamp of current billing period start */
  current_period_start?: number | null;
  /** Stripe customer ID */
  customer_id?: string | null;
  /** Default payment method ID */
  default_payment_method?: string | null;
  /** Whether the organization has an active Stripe subscription */
  has_subscription: boolean;
  /** Subscription line items including price info */
  items?:
    | {
        [key: string]: any;
      }[]
    | null;
  /** Latest invoice ID */
  latest_invoice?: string | null;
  /** Stripe subscription metadata */
  metadata?: {
    [key: string]: any;
  } | null;
  /** Number of seats/users in subscription */
  quantity?: number | null;
  /** Subscription status (active, trialing, past_due, canceled, etc.) */
  status?: string | null;
  /** Stripe subscription ID */
  subscription_id?: string | null;
  /** Unix timestamp of trial end */
  trial_end?: number | null;
  /** Unix timestamp of trial start */
  trial_start?: number | null;
};
export type BillingInfo = {
  accept_insurance_payment_as_full?: string | null;
  place_of_service?: string | null;
  tax_id?: string | null;
  tax_id_type?: string | null;
};
export type EntitlementResponse = {
  /** Source of the assignment (stripe, manual, trial) */
  assignment_source: string;
  /** Bundle UUID that granted this entitlement */
  bundle_id: string;
  /** Name of the bundle */
  bundle_name: string;
  /** Description of the capability */
  capability_description?: string | null;
  /** Human-friendly display name for the capability */
  capability_display_name?: string | null;
  /** Capability UUID */
  capability_id: string;
  /** Name of the capability */
  capability_name: string;
  /** End of current usage period */
  current_period_end?: string | null;
  /** Start of current usage period */
  current_period_start?: string | null;
  /** Usage count in current period (null if not tracked) */
  current_usage?: number | null;
  /** Metering type (none, fully_metered, hybrid) */
  metering_type: string;
  /** Usage quota (null = unlimited) */
  quota?: number | null;
  /** Remaining quota (null if unlimited) */
  quota_remaining?: number | null;
  /** Period for quota reset (daily, weekly, monthly) */
  usage_period?: string | null;
};
export type FacilityInfo = {
  city?: string | null;
  facility_address?: string | null;
  facility_address2?: string | null;
  state?: string | null;
  zip?: string | null;
};
export type OrganizationInfo = {
  /** True if organization has more than one user (active + inactive) */
  group: boolean;
  /** Number of active users in the organization */
  size: number;
};
export type PersonalInfo = {
  communications_name?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  signing_name?: string | null;
};
export type PracticeInfo = {
  billing_npi_number?: string | null;
  fax_number?: string | null;
  license_number?: string | null;
  license_state?: string | null;
  license_type?: string | null;
  npi_number?: string | null;
  specialization?: string | null;
};
export type ReferralInfo = {
  /** User's referral code */
  referral_code?: string | null;
  /** Earnings from referrals */
  referral_earnings?: string | null;
  /** Code user was referred with */
  referred_by_code?: string | null;
  /** ID of the user who referred them */
  referred_by_user_id?: string | null;
  /** User IDs referred by this user */
  referred_users?: string[] | null;
};
export type UserMetrics = {
  /** Total number of API calls made */
  api_calls_made?: number | null;
  /** Total number of comments provided by the user */
  comments_provided?: number | null;
  /** Total number of documents created */
  documents_created?: number | null;
  /** Total number of logins */
  login_count?: number | null;
  /** Total minutes of audio transcribed */
  minutes_transcribed?: number | null;
  /** Total number of notes copied by the user */
  notes_copied?: number | null;
  /** Total number of notes created by the user */
  notes_created?: number | null;
  /** Total number of AI-generated notes */
  notes_generated?: number | null;
  /** Total number of ratings provided by the user */
  ratings_provided?: number | null;
  /** Total number of completed sessions */
  sessions_completed?: number | null;
  /** Total number of templates used */
  templates_used?: number | null;
  /** Total number of AI tokens consumed */
  tokens_consumed?: number | null;
  /** Total time saved in seconds */
  total_time_saved?: number | null;
  /** Total number of transcription sessions */
  transcription_sessions?: number | null;
  /** Total number of transcriptions processed */
  transcriptions_processed?: number | null;
};
export type UserNotification = {
  /** Unix timestamp */
  created_at?: number | null;
  /** External URL link */
  external_url?: string | null;
  /** Internal URL link */
  internal_url?: string | null;
  /** 0 = unopened, 1 = opened */
  is_opened?: number | null;
  /** Unique notification ID */
  notification_id?: string | null;
  /** Name of requester */
  requester_name?: string | null;
  /** Notification body text */
  text?: string | null;
  /** Notification title */
  title?: string | null;
  /** Hover tooltip text */
  tooltip?: string | null;
  /** Notification type */
  type?: string | null;
};
export type CopyPastePreferences = {
  html_formatting_on_copy?: boolean;
  include_header_on_copy_multiple?: boolean;
};
export type CustomDocumentPrompt = {
  created_at: string;
  description?: string | null;
  document_type: string;
  name: string;
  prompt: string;
  prompt_id: string;
  updated_at?: string | null;
};
export type CustomLabBundle = {
  bundle_id: string;
  created_at: string;
  description?: string | null;
  name: string;
  special_instructions?: string | null;
  test_names: string[];
  updated_at?: string | null;
};
export type DocumentPreferences = {
  letterhead_md_string?: string | null;
};
export type JotCoachPreferences = {
  minimum_nudge_minutes?: number | null;
};
export type WebRecordingPreferences = {
  stop_mode?: string | null;
};
export type RecordingPreferences = {
  redact_names?: boolean;
  web_recording?: WebRecordingPreferences | null;
};
export type UserPreferencesResponse = {
  copy_paste_preferences?: CopyPastePreferences;
  custom_document_prompts?: CustomDocumentPrompt[];
  custom_lab_bundles?: CustomLabBundle[];
  document_preferences?: DocumentPreferences;
  jotcoach_preferences?: JotCoachPreferences;
  recording_preferences?: RecordingPreferences;
  subject?: string;
  transcription_languages?: string[];
};
export type UserInfoResponse = {
  /** Whether user has accepted terms of service */
  accepted_tos?: boolean;
  /** Tax and billing preferences */
  billing_info?: BillingInfo;
  /** Whether user has consented to SMS */
  checked_sms_consent?: boolean;
  /** When the user account was created */
  created_at?: string | null;
  /** User's timezone */
  default_timezone?: string | null;
  /** DoseSpot IDP onboarding status */
  dosespot_onboarding_status?: string | null;
  /** Whether email is verified */
  email_verified?: boolean;
  /** User's entitlements (capabilities they have access to) */
  entitlements?: EntitlementResponse[];
  /** Physical practice location details */
  facility_info?: FacilityInfo;
  /** Feature flags for this user */
  feature_flags?: {
    [key: string]: boolean;
  };
  /** HMAC hash for Front chatbot verification */
  front_chatbot_hash?: string | null;
  /** Whether Google Calendar is integrated */
  google_calendar_integrated?: boolean;
  /** Onboarding completion flags */
  onboarding_steps?: {
    [key: string]: boolean;
  };
  /** Organization ID the user belongs to */
  organization_id?: string | null;
  /** Organization details including member count */
  organization_info?: OrganizationInfo | null;
  /** User's subscription/payment status */
  payment_status?: string | null;
  /** Personal contact information */
  personal_info?: PersonalInfo;
  /** Professional identifiers and practice details */
  practice_info?: PracticeInfo;
  /** Referral program data */
  referral_info?: ReferralInfo | null;
  /** User's roles in the organization */
  roles?: string[];
  /** Stripe Connect account ID */
  stripe_connect_account_id?: string | null;
  /** User's ID */
  user_id: string;
  /** User usage and engagement metrics */
  user_metrics?: UserMetrics | null;
  /** Active notifications */
  user_notifications?: UserNotification[];
  /** User preferences including recording, document, and action settings */
  user_preferences?: UserPreferencesResponse;
};
export type CreateUserResponse = {
  /** True if user joined an existing organization via invitation */
  joined_existing_org?: boolean;
  /** The user's organization ID */
  organization_id: string;
  /** Stripe customer ID (only for new organizations) */
  stripe_customer_id?: string | null;
  /** Stripe subscription ID (only for new organizations) */
  stripe_subscription_id?: string | null;
  /** The created user's ID */
  user_id: string;
};
export type CreateUserRequest = {
  /** How the user discovered the product */
  discovery_channel?: string | null;
  /** Additional discovery channel details */
  discovery_channel_text?: string | null;
  /** User's email address */
  email: string;
  /** Invitation token to join existing organization */
  invite_token?: string | null;
  /** Signup platform (web, ios, etc.) */
  platform?: string | null;
  /** UTM tracking parameters */
  utm?: {
    [key: string]: string;
  } | null;
};
